import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Tooltip, message } from 'antd';
import { useDrag } from 'react-dnd';
import { useSelector } from 'react-redux';
import { isValid, isValidArray } from '../../utils/utilities';
import { fetchNotBoardedSeats } from '../../actions/reports.action';
import { selectedScheduleSelector } from '../../selector/home.selector';
import { extractPassengerInfo, TooltipContent } from './common-seat-layout';

export const NOT_BOARDED_SEAT_DND_TYPE = 'NOT_BOARDED_SEAT';

const isLadiesSeat = (seat) => seat.gender === 'F';

const getGender = (seatGender) => {
    if (isValid(seatGender) && seatGender === 'F') {
        return '(' + seatGender + ')';
    }
    return '';
};

const buildSeatsMap = (seatsData) => {
    const map = {};
    if (isValidArray(seatsData)) {
        seatsData.forEach((seat) => {
            if (isValid(seat) && isValid(seat.seat_number) && seat.seat_number !== 'Empty') {
                map[seat.seat_number] = seat;
            }
        });
    }
    return map;
};

const NotBoardedSeatChip = ({ seat }) => {

    // Force the tooltip's "booked by" line to read "Booked by ..." instead of
    // the cancelled traveller_status that getBookedName would otherwise pick up.
    const tooltipSeat = { ...seat, traveller_status: 'Booked' };
    const tooltipProps = extractPassengerInfo(tooltipSeat, 'Booked');
    const ladies = isLadiesSeat(seat);
    const statusClassName = isValid(seat.seat_status_class_name) ? seat.seat_status_class_name : '';

    const [tooltipVisible, setTooltipVisible] = useState(false);

    const dragRef = useRef(null);
    const [{ isDragging }, drag] = useDrag({
        type: NOT_BOARDED_SEAT_DND_TYPE,
        item: () => {
            setTooltipVisible(false);
            return {
                ticket_id: seat.ticket_id,
                travellerId: seat.travellerId,
                seat_number: seat.seat_number,
                full_name: seat.full_name,
                gender: seat.gender,
                trip_id: seat.trip_id,
                trip_route_id: seat.trip_route_id,
                isNotBoarded: true
            };
        },
        canDrag: () => isValid(seat.ticket_id),
        collect: (monitor) => ({
            isDragging: monitor.isDragging()
        })
    });
    drag(dragRef);

    const chipClass = `${seat.sleeper == 1 ? 'sleeper':'seater'} not-boarded-seat-chip ${statusClassName} ${ladies ? 'not-boarded-seat-chip--ladies' : ''}`;
    const chipStyle = {
        ...(isValid(seat.seat_status_color) ? { backgroundColor: seat.seat_status_color } : {}),
        cursor: 'move',
        opacity: isDragging ? 0.5 : 1
    };
    return (
        <Tooltip
            placement="left"
            mouseEnterDelay={0.4}
            visible={!isDragging && tooltipVisible}
            onVisibleChange={(v) => {
                if (isDragging) {
                    setTooltipVisible(false);
                } else {
                    setTooltipVisible(v);
                }
            }}
            title={<TooltipContent {...tooltipProps} passengerNo={null} />}
        >
            <div
                ref={dragRef}
                className={chipClass}
                style={chipStyle}
                onMouseDown={() => setTooltipVisible(false)}
                onDragStart={() => setTooltipVisible(false)}
            >
                <div className="seat-number">{seat.seat_number}</div>
                {ladies ? <div className="seat-gender">{getGender(seat.gender)}</div> : null}
            </div>
        </Tooltip>
    );
};

export const NotBoardedSeatsBlock = ({ tripId, seatsData, reloadSignal }) => {
    const [seats, setSeats] = useState([]);
    const selectedSchedule = useSelector(selectedScheduleSelector);

    const effectiveTripId = tripId || (isValid(selectedSchedule) ? selectedSchedule.trip_id : null);

    useEffect(() => {
        if (!isValid(effectiveTripId)) {
            setSeats([]);
            return;
        }
        fetchNotBoardedSeats(effectiveTripId, (data) => {
            setSeats(isValidArray(data) ? data : []);
        }, (msg) => {
            if (isValid(msg)) {
                message.error(msg);
            }
            setSeats([]);
        });
    }, [effectiveTripId, reloadSignal]);

    const seatsByNumber = useMemo(() => buildSeatsMap(seatsData), [seatsData]);

    if (!isValidArray(seats) || seats.length === 0) {
        return null;
    }

    return (
        <div className="not-boarded-block">
            <div className="not-boarded-header">
                <span className="not-boarded-title">Not Boarded</span>
                <span className="not-boarded-count">{seats.length} seats</span>
            </div>
            <div className="not-boarded-seats-row">
                {seats.map((seat) => {
                    const layoutSeat = seatsByNumber[seat.seat_number] || {};
                    const mergedSeat = { ...layoutSeat, ...seat, sleeper: layoutSeat.sleeper };
                    return (
                        <NotBoardedSeatChip
                            key={seat.travellerId || `${seat.ticket_id}-${seat.seat_number}`}
                            seat={mergedSeat}
                        />
                    );
                })}
            </div>
        </div>
    );
};
